/**
 * DF-0423 — l'heure d'abord, puis les minutes. Miroir de
 * ouitap-showcase `src/lib/sections/slotPicker.ts` (même règle, deux écrans).
 *
 * Au pas de 5 min (SC-1147), lister à plat les créneaux du hub donne 105
 * boutons chez Yuji, 140 chez Fuuki. On les range donc en services → heures
 * → minutes : le convive touche « 12 h », puis « :10 ». Rien ne change dans
 * ce qui est posté : chaque minute proposée est un créneau de la liste.
 *
 * Le hub ne nomme pas ses services : un trou dans la grille (écart entre deux
 * créneaux consécutifs plus grand que le pas) suffit à séparer midi du soir.
 * On le cherche dans la grille COMPLÈTE, pas dans les seuls créneaux libres —
 * un créneau complet au milieu du service ne doit pas couper le service.
 */

export type SlotHour = {
  /** « 12 » — préfixe heure des créneaux, tel que le hub l'écrit. */
  hour: string;
  /** Créneaux libres de cette heure, « HH:MM », dans l'ordre du hub. */
  times: string[];
};

export type SlotService = {
  /** Midi ou soir d'après l'heure du premier créneau ; null quand deux
   *  services tomberaient sous le même nom — on affiche alors from – to. */
  period: 'lunch' | 'dinner' | null;
  from: string;
  to: string;
  hours: SlotHour[];
};

function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** Écart de a vers b sur l'horloge, minuit traversé compris (23:55 → 00:00 = 5). */
function gap(a: string, b: string): number {
  return (minutesOf(b) - minutesOf(a) + 1440) % 1440;
}

export function groupSlots(allTimes: string[], availableTimes: string[]): SlotService[] {
  if (allTimes.length === 0) return [];
  const gaps = allTimes
    .slice(1)
    .map((t, i) => gap(allTimes[i], t))
    .filter((g) => g > 0);
  const step = gaps.length ? Math.min(...gaps) : 0;

  const runs: string[][] = [[allTimes[0]]];
  for (let i = 1; i < allTimes.length; i++) {
    const g = gap(allTimes[i - 1], allTimes[i]);
    if (g > step || g === 0) runs.push([]);
    runs[runs.length - 1].push(allTimes[i]);
  }

  const free = new Set(availableTimes);
  const services = runs
    .map((run) => {
      const hours: SlotHour[] = [];
      for (const time of run) {
        if (!free.has(time)) continue;
        const hour = time.slice(0, 2);
        const last = hours[hours.length - 1];
        if (last?.hour === hour) last.times.push(time);
        else hours.push({ hour, times: [time] });
      }
      return { start: run[0], hours };
    })
    .filter((s) => s.hours.length > 0);

  const periods: Array<'lunch' | 'dinner'> = services.map((s) =>
    minutesOf(s.start) < 16 * 60 ? 'lunch' : 'dinner',
  );
  const distinct = new Set(periods).size === periods.length;
  return services.map((s, i) => ({
    period: distinct ? periods[i] : null,
    from: s.hours[0].times[0],
    to: s.hours[s.hours.length - 1].times.at(-1) as string,
    hours: s.hours,
  }));
}

/**
 * DF-0427 — une touche sur l'heure suffit : elle choisit un créneau. Avant,
 * « 11 h » s'allumait sans rien retenir ; le convive croyait avoir fini et
 * ne lisait « Veuillez choisir un créneau » qu'à l'envoi. On garde le créneau
 * déjà choisi s'il tombe dans cette heure, sinon on prend le premier ; les
 * minutes ne servent plus qu'à affiner.
 */
export function slotForHour(times: string[], current: string | null | undefined): string | null {
  return current && times.includes(current) ? current : (times[0] ?? null);
}
